import { DOCUMENT, isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { AuthService } from 'zenith/auth';
import { REQUEST, RESPONSE } from 'zenith/context';

import { TENANTS_STATE_TOKEN } from '@core/token/tenant.token';

const AUTH_LOGIN_URL = '/auth/login?redirect=' + encodeURIComponent('/login');

function redirect(target: string, platform: object): void {
  if (isPlatformServer(platform)) {
    inject(RESPONSE, { optional: true })?.redirect(target);
    return;
  }
  inject(DOCUMENT).defaultView?.location.assign(target);
}

/**
 * Builds an absolute URL to another tenant's subdomain, keeping the current
 * protocol and port. Without this, cross-subdomain redirects hardcode https
 * with no port and break local dev, where every host runs behind a
 * non-default port (e.g. local.sochtech.co:4000 -> villa-paraiso.sochtech.co:4000).
 */
function buildTenantUrl(tenantHost: string, path: string, platform: object): string {
  if (isPlatformServer(platform)) {
    const request = inject(REQUEST, { optional: true });
    const protocol = request?.protocol ?? 'https';
    const port = request?.get('host')?.split(':')[1];
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}://${tenantHost}${portSuffix}${path}`;
  }

  const location = inject(DOCUMENT).defaultView?.location;
  const protocol = location?.protocol ?? 'https:';
  const port = location?.port;
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${tenantHost}${portSuffix}${path}`;
}

/**
 * '/login' is a pure redirect, never a screen: no session -> Keycloak login; session with
 * tenants -> straight to the first available tenant's app (cross-subdomain, so it's a full
 * navigation, not an Angular route); session with zero tenants -> the existing '/unauthorized'
 * fallback.
 */
export function loginRedirectGuard(): boolean | UrlTree {
  const authService = inject(AuthService);
  const tenantsState = inject(TENANTS_STATE_TOKEN);
  const platform = inject(PLATFORM_ID);

  const user = authService.getCurrentUser();

  if (!user) {
    redirect(AUTH_LOGIN_URL, platform);
    return false;
  }

  const tenant = tenantsState.tenants[0];

  if (!tenant) {
    if (isPlatformServer(platform)) {
      redirect('/unauthorized', platform);
      return false;
    }
    return inject(Router).createUrlTree(['/unauthorized']);
  }

  redirect(buildTenantUrl(tenant.domain, '/app', platform), platform);
  return false;
}
